import React, { FC, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"
import { ModalConfirmAction } from "components/modals/ModalConfirmAction"
import { Column, DataTable } from "components/tables/DataTable"

import { postbacksService } from "../../services/postbacks/postbacks.service"
import { Postback, TestFireResult } from "../../services/postbacks/type"

import { PostbackEditorModal } from "./PostbackEditorModal"

export const PostbackList: FC = () => {
  const { t } = useTranslation(["translation"], { useSuspense: false })
  const [rows, setRows] = useState<Postback[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Postback | null>(null)
  const [showEditor, setShowEditor] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<Postback | null>(null)
  const [testResult, setTestResult] = useState<{ name: string; result: TestFireResult } | null>(null)
  const [testingId, setTestingId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    setRows(await postbacksService.list())
    setLoading(false)
  }

  useEffect(() => {
    void load()
  }, [])

  const openNew = () => {
    setEditing(null)
    setShowEditor(true)
  }

  const openEdit = (row: Postback) => {
    setEditing(row)
    setShowEditor(true)
  }

  const onSaved = () => {
    setShowEditor(false)
    void load()
  }

  const doDelete = async (row: Postback) => {
    if (await postbacksService.remove(row.id)) void load()
  }

  const test = async (row: Postback) => {
    setTestingId(row.id)
    const result = await postbacksService.test(row.id)
    setTestingId(null)
    if (result) setTestResult({ name: row.name, result })
  }

  const columns: Column<Postback>[] = [
    { header: t("s2sPostbacks.list.name"), render: (row) => row.name },
    { header: t("s2sPostbacks.list.event"), render: (row) => <code className="text-purple">{row.event}</code> },
    { header: t("s2sPostbacks.list.method"), render: (row) => row.method },
    {
      header: t("s2sPostbacks.list.filters"),
      render: (row) =>
        row.filters.length === 0 ? (
          <span className="opacity-60">{t("s2sPostbacks.list.global")}</span>
        ) : (
          `${row.filters.length} (${row.filter_mode})`
        )
    },
    {
      header: t("s2sPostbacks.list.status"),
      render: (row) =>
        row.enabled ? (
          <span className="badge badge-sm border-0 bg-emerald-500/15 text-emerald-400">{t("s2sPostbacks.list.enabled")}</span>
        ) : (
          <span className="badge badge-sm badge-ghost">{t("s2sPostbacks.list.disabled")}</span>
        )
    },
    {
      header: t("s2sPostbacks.list.actions"),
      align: "right",
      render: (row) => (
        <div className="flex justify-end gap-1">
          <button className="btn btn-ghost btn-xs" onClick={() => test(row)} disabled={testingId === row.id} type="button">
            {testingId === row.id ? t("s2sPostbacks.list.testing") : t("s2sPostbacks.list.test")}
          </button>
          <button className="btn btn-ghost btn-xs" onClick={() => openEdit(row)} type="button">
            {t("s2sPostbacks.list.edit")}
          </button>
          <button className="btn btn-ghost btn-xs text-rose-400" onClick={() => setPendingDelete(row)} type="button">
            {t("s2sPostbacks.list.delete")}
          </button>
        </div>
      )
    }
  ]

  return (
    <div className="rounded-xl border border-[rgba(0,0,0,0.05)] dark:border-[rgba(255,255,255,0.1)] bg-white dark:bg-[#0C0C1A] p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t("s2sPostbacks.list.title")}</h2>
        <button className="btn btn-primary btn-sm" onClick={openNew} type="button">
          {t("s2sPostbacks.list.new")}
        </button>
      </div>

      {loading ? (
        <p className="text-sm opacity-60">{t("s2sPostbacks.list.loading")}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm opacity-50">{t("s2sPostbacks.list.empty")}</p>
      ) : (
        <DataTable rows={rows} columns={columns} rowKey={(row) => row.id} />
      )}

      {showEditor && <PostbackEditorModal initial={editing} onClose={() => setShowEditor(false)} onSaved={onSaved} />}

      <ModalConfirmAction
        data={pendingDelete}
        handler={() => setPendingDelete(null)}
        text={pendingDelete ? t("s2sPostbacks.list.confirmDelete", { name: pendingDelete.name }) : ""}
        actionText={t("s2sPostbacks.list.delete")}
        actionHandler={doDelete}
      />

      <Modal open={Boolean(testResult)} handler={() => setTestResult(null)}>
        {testResult && (
          <div className="modal-box w-full max-w-xl">
            <h3 className="mb-3 text-lg font-semibold">{t("s2sPostbacks.test.title", { name: testResult.name })}</h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="opacity-60">{t("s2sPostbacks.test.resolvedUrl")}</span>
                <div className="mt-1 break-all rounded bg-base-300 p-2 font-mono text-xs">{testResult.result.resolved_url}</div>
              </div>
              <p>
                <span className="opacity-60">{t("s2sPostbacks.test.result")}</span>{" "}
                <span className={testResult.result.success ? "text-emerald-400" : "text-rose-400"}>
                  {testResult.result.success ? t("s2sPostbacks.test.success") : t("s2sPostbacks.test.failed")}
                  {testResult.result.http_status !== null && ` (HTTP ${testResult.result.http_status})`}
                </span>
              </p>
              {testResult.result.error && (
                <p className="text-rose-400">{t("s2sPostbacks.test.error", { error: testResult.result.error })}</p>
              )}
              {testResult.result.response_body && (
                <div>
                  <span className="opacity-60">{t("s2sPostbacks.test.responseBody")}</span>
                  <pre className="mt-1 max-h-40 overflow-auto rounded bg-base-300 p-2 text-xs">{testResult.result.response_body}</pre>
                </div>
              )}
            </div>
            <div className="modal-action">
              <button className="btn btn-primary btn-sm" onClick={() => setTestResult(null)} type="button">
                {t("s2sPostbacks.test.close")}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
